/**
 * Back Roads: typed accessors over the authored content and the baked
 * coordinates.
 *
 * The content (eras, places, routes, trips) is written by hand in this folder.
 * The coordinates come from `generated/backroads-places.json`, which the bake
 * resolves by name; the map geometry is in `generated/backroads-map.json` and
 * is fetched by the client on demand, the way the subdivision polygons are,
 * because it is a few hundred kilobytes that three of four modes never draw.
 */

import type { LngLat } from '@geogame/shared';

import placesJson from '../generated/backroads-places.json' with { type: 'json' };
import { ANCHORS, PLACES } from './places';
import { ROUTES } from './routes';
import { TRIPS } from './trips';
import type { Place, PlaceSeed, RouteDef, Trip } from './types';

export type {
  Era,
  EraId,
  EstimateUnit,
  Place,
  PlaceKind,
  PlaceSeed,
  Question,
  Region,
  RegionId,
  RouteDef,
  RouteMode,
  Stop,
  Trip,
  WaterDef,
} from './types';
export { ERAS, REGIONS, eraById, eraForYear, regionById } from './eras';
export { ANCHORS, PLACES } from './places';
export { ROUTES } from './routes';
export { TRIPS } from './trips';
export { WATER } from './water';

interface BakedPlace {
  readonly id: string;
  readonly lngLat: LngLat;
  /** False when the bake kept the authored coordinate. */
  readonly resolved: boolean;
}

// Typed as `unknown` on purpose: see src/generated/generated.d.ts.
const BAKED = placesJson as unknown as readonly BakedPlace[];
const BAKED_INDEX: ReadonlyMap<string, BakedPlace> = new Map(BAKED.map((p) => [p.id, p] as const));

function resolve(seed: PlaceSeed): Place {
  const baked = BAKED_INDEX.get(seed.id);
  return { ...seed, lngLat: baked ? baked.lngLat : seed.approx };
}

const RESOLVED: readonly Place[] = PLACES.map(resolve);
const PLACE_INDEX: ReadonlyMap<string, Place> = new Map(RESOLVED.map((p) => [p.id, p] as const));

/** Every place, with its resolved coordinate. */
export function allPlaces(): readonly Place[] {
  return RESOLVED;
}

export function placeById(id: string): Place {
  const place = PLACE_INDEX.get(id);
  if (!place) throw new Error(`backroads: unknown place ${id}`);
  return place;
}

/** Whether the bake found a mapped point for this place. For tests and the bake report. */
export function placeWasResolved(id: string): boolean {
  return BAKED_INDEX.get(id)?.resolved === true;
}

const TRIP_INDEX: ReadonlyMap<string, Trip> = new Map(TRIPS.map((t) => [t.id, t] as const));

export function tripById(id: string): Trip | undefined {
  return TRIP_INDEX.get(id);
}

/** Is this place one of the labelled anchors the map always shows? */
export function isAnchor(id: string): boolean {
  return ANCHORS.includes(id);
}

/**
 * Whether a dated thing is on the map in a given year. `from` is inclusive,
 * `to` exclusive: a reservoir that filled in 1930 is drawn for 1930, and a
 * village that went under that year is not.
 */
export function existsIn(year: number, from: number | undefined, to: number | undefined): boolean {
  if (from !== undefined && year < from) return false;
  if (to !== undefined && year >= to) return false;
  return true;
}

/** The routes drawn for a year. */
export function routesFor(year: number): readonly RouteDef[] {
  return ROUTES.filter((r) => existsIn(year, r.from, r.to));
}

/** A route as a polyline of resolved coordinates. */
export function routeLine(route: RouteDef): readonly LngLat[] {
  return route.via.map((id) => placeById(id).lngLat);
}
